import { useEffect, useState } from "react";
import {
  listCheckpoints,
  createCheckpoint,
  deleteCheckpoint,
  restoreCheckpoint,
  CheckpointSummary,
  RestoreOptions
} from "../../api/admin";

export default function CheckpointsPage() {
  const [checkpoints, setCheckpoints] = useState<CheckpointSummary[]>([]);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const [restoreFor, setRestoreFor] = useState<CheckpointSummary | null>(null);
  const [options, setOptions] = useState<RestoreOptions>({
    restoreSubmissions: true,
    restoreFormConfig: false,
    restoreSettings: false
  });

  const load = () => {
    listCheckpoints()
      .then(setCheckpoints)
      .catch((e) => setError(e instanceof Error ? e.message : "Failed to load checkpoints"));
  };

  useEffect(load, []);

  const onCreate = async () => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const next = await createCheckpoint(label.trim());
      setCheckpoints(next);
      setLabel("");
      setMessage("Checkpoint created.");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Create failed");
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (id: string) => {
    if (!window.confirm("Delete this checkpoint? This cannot be undone.")) return;
    try {
      const next = await deleteCheckpoint(id);
      setCheckpoints(next);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Delete failed");
    }
  };

  const openRestore = (cp: CheckpointSummary) => {
    setRestoreFor(cp);
    setOptions({
      restoreSubmissions: true,
      restoreFormConfig: cp.hasFormConfig,
      restoreSettings: cp.hasSettings
    });
    setMessage(null);
    setError(null);
  };

  const confirmRestore = async () => {
    if (!restoreFor) return;
    if (!options.restoreSubmissions && !options.restoreFormConfig && !options.restoreSettings) {
      setError("Select at least one thing to restore.");
      return;
    }
    if (
      !window.confirm(
        "Restore from this checkpoint? Selected data will be overwritten with the checkpoint's snapshot."
      )
    )
      return;
    setBusy(true);
    setError(null);
    try {
      await restoreCheckpoint(restoreFor.id, options);
      setMessage("Restore complete. Reload other pages to see restored data.");
      setRestoreFor(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Restore failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="admin-page-head">
        <h1>Version history</h1>
      </div>
      <p className="hint">
        Create a checkpoint to snapshot all submissions, the form configuration, and settings. You can restore any
        checkpoint later and choose what to bring back.
      </p>

      {error && <div className="banner-error">{error}</div>}
      {message && <div className="banner-success">{message}</div>}

      <div className="settings-form" style={{ marginBottom: "1.5rem" }}>
        <label className="field-label">New checkpoint label</label>
        <div style={{ display: "flex", gap: ".5rem", flexWrap: "wrap" }}>
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. Before Q3 form changes"
            style={{ flex: 1, minWidth: "220px", padding: ".5rem" }}
          />
          <button type="button" className="btn-save" disabled={busy} onClick={onCreate}>
            {busy ? "Working…" : "Create checkpoint"}
          </button>
        </div>
      </div>

      {checkpoints.length === 0 ? (
        <p className="hint">No checkpoints yet.</p>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Label</th>
              <th>Created</th>
              <th>By</th>
              <th>Contents</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {checkpoints.map((cp) => (
              <tr key={cp.id}>
                <td>{cp.label}</td>
                <td>{new Date(cp.createdAt).toLocaleString()}</td>
                <td>{cp.createdBy}</td>
                <td>
                  {cp.submissionCount} submission{cp.submissionCount === 1 ? "" : "s"}
                  {cp.hasFormConfig ? ", form" : ""}
                  {cp.hasSettings ? ", settings" : ""}
                </td>
                <td style={{ whiteSpace: "nowrap" }}>
                  <button type="button" className="btn-secondary btn-sm" onClick={() => openRestore(cp)}>
                    Restore…
                  </button>{" "}
                  <button type="button" className="btn-danger btn-sm" onClick={() => onDelete(cp.id)}>
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {restoreFor && (
        <div className="modal-overlay" onClick={() => !busy && setRestoreFor(null)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <h3>Restore “{restoreFor.label}”</h3>
            <p className="hint">Choose what to restore. Existing data for selected items will be replaced.</p>
            <label className="perm-check">
              <input
                type="checkbox"
                checked={options.restoreSubmissions}
                onChange={(e) => setOptions((o) => ({ ...o, restoreSubmissions: e.target.checked }))}
              />
              Submissions ({restoreFor.submissionCount})
            </label>
            <label className="perm-check">
              <input
                type="checkbox"
                checked={options.restoreFormConfig}
                disabled={!restoreFor.hasFormConfig}
                onChange={(e) => setOptions((o) => ({ ...o, restoreFormConfig: e.target.checked }))}
              />
              Form questions &amp; sections
            </label>
            <label className="perm-check">
              <input
                type="checkbox"
                checked={options.restoreSettings}
                disabled={!restoreFor.hasSettings}
                onChange={(e) => setOptions((o) => ({ ...o, restoreSettings: e.target.checked }))}
              />
              Settings (progress stages &amp; notifications)
            </label>
            <div className="actions" style={{ marginTop: "1rem" }}>
              <button type="button" className="btn-save" disabled={busy} onClick={confirmRestore}>
                {busy ? "Restoring…" : "Restore"}
              </button>
              <button type="button" className="btn-secondary" disabled={busy} onClick={() => setRestoreFor(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
